/**
 * Plain words for the Friction screen: what each kind of problem is called, what its groups are
 * grouped by, and how one occurrence's stored detail reads (a status and a title, a short
 * message, a reason, a destination). Never anything beyond what the event already stored.
 */
import type { FrictionGroup, FrictionKindName, FrictionOccurrence } from './friction-types';
import { screenWord } from '../usage/words';

interface KindWords {
  title: string;
  /** What the bars are grouped by, for the chart's spoken label. */
  by: string;
  /** The sentence under the title. */
  sentence: string;
}

export const frictionKindWords: Record<FrictionKindName, KindWords> = {
  'request.failed': {
    title: 'Refused requests',
    by: 'screen',
    sentence: 'answers the browser got back with an error, by the screen they happened on',
  },
  'browser.error': {
    title: 'Errors the browser saw',
    by: 'screen',
    sentence: 'uncaught errors in the app, by screen',
  },
  'scan.failed': {
    title: 'Failed scans',
    by: 'reason',
    sentence: 'scans that did not finish, by reason',
  },
  'pages.upload_failed': {
    title: 'Failed uploads',
    by: 'reason',
    sentence: 'uploads that did not finish, by reason',
  },
  'export.failed': {
    title: 'Failed exports',
    by: 'destination',
    sentence: 'deliveries that failed, by destination',
  },
  'review.withdrawn': {
    title: 'Reviews withdrawn',
    by: 'kind',
    sentence: 'confirmations a reviewer took back',
  },
};

const reasonWords: Record<string, string> = {
  unknown: 'No reason given',
  'paper-jam': 'Paper jam',
  'no-scanner': 'No scanner found',
  'not-paired': 'Scanner not paired',
  cancelled: 'Cancelled',
  'too-large': 'File too large',
  'wrong-type': 'Unsupported file type',
  network: 'Lost the connection',
  timeout: 'Timed out',
  refused: 'Refused by the server',
};

const destinationWords: Record<string, string> = {
  unknown: 'No destination',
  sftp: 'SFTP',
  webhook: 'Webhook',
  email: 'Email',
  'content-central': 'Content Central',
  'nucleus-one': 'Nucleus One',
  'microsoft-365': 'Microsoft 365',
  'google-drive': 'Google Drive',
  dropbox: 'Dropbox',
  box: 'Box',
  gdrive: 'Google Drive',
  onedrive: 'OneDrive',
  sharepoint: 'SharePoint',
  s3: 'Amazon S3',
  gcs: 'Cloud Storage',
};

function tidy(raw: string): string {
  const words = raw.replace(/[._-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}

/** The words for one group's key: a screen name, a reason, a destination. */
export function frictionKeyWord(kind: FrictionKindName, key: string): string {
  switch (kind) {
    case 'request.failed':
    case 'browser.error':
      return screenWord(key);
    case 'scan.failed':
    case 'pages.upload_failed':
      return reasonWords[key] ?? tidy(key);
    case 'export.failed':
      return destinationWord(key);
    case 'review.withdrawn':
      return 'All reviews';
  }
}

/**
 * A destination key as the delivery ledger writes it ("content-central-de69df", "box-a17363"): the kind in
 * plain words, without the id fragment nobody can read.
 */
function destinationWord(key: string): string {
  const kind = Object.keys(destinationWords)
    .filter((k) => key === k || key.startsWith(`${k}-`))
    .sort((a, b) => b.length - a.length)[0];
  return kind ? destinationWords[kind]! : tidy(key);
}

/**
 * One group's bar label: for a failed export the company and the destination's own label ("Dustland ·
 * Content Central (AP)"), for the rest the key's words, with the company in front when one company is
 * behind all of it.
 */
export function frictionGroupWord(
  kind: FrictionKindName,
  g: Pick<FrictionGroup, 'key' | 'label' | 'company'>,
): string {
  const what =
    kind === 'export.failed' ? (g.label ?? destinationWord(g.key)) : frictionKeyWord(kind, g.key);
  return g.company ? `${g.company.name} · ${what}` : what;
}

/** "403 Two-step sign-in required" as "mostly Two-step sign-in required (403)": the words first, the code after. */
export function mostlyWord(top: string): string {
  const m = /^(\d{3})\s+(.+)$/.exec(top);
  return m ? `mostly ${m[2]} (${m[1]})` : `mostly ${top}`;
}

function text(detail: Record<string, unknown> | null, key: string): string | null {
  const v = detail?.[key];
  return typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : null;
}

type Detail = Record<string, unknown> | null;

function refusedWords(d: Detail): string {
  const parts = [text(d, 'status'), text(d, 'title')].filter((p): p is string => p !== null);
  return parts.length ? `Refused (${parts.join(', ')})` : 'Refused';
}

function browserErrorWords(d: Detail): string {
  return text(d, 'message') ?? (text(d, 'kind') === 'rejection' ? 'Unhandled rejection' : 'Error');
}

function reasonWordsOf(kind: FrictionKindName): (d: Detail) => string {
  return (d) => frictionKeyWord(kind, text(d, 'reason') ?? 'unknown');
}

function exportFailedWords(d: Detail): string {
  const where = destinationWord(text(d, 'destination') ?? 'unknown');
  return d?.['final'] === true ? `${where}, gave up` : `${where}, will retry`;
}

function withdrawnWords(d: Detail): string {
  const n = text(d, 'documents');
  return n ? `${n} document${n === '1' ? '' : 's'}` : 'A confirmation';
}

const detailWords: Record<FrictionKindName, (d: Detail) => string> = {
  'request.failed': refusedWords,
  'browser.error': browserErrorWords,
  'scan.failed': reasonWordsOf('scan.failed'),
  'pages.upload_failed': reasonWordsOf('pages.upload_failed'),
  'export.failed': exportFailedWords,
  'review.withdrawn': withdrawnWords,
};

/** One occurrence's detail in plain words: "Refused (409, NameTaken)", "TypeError: x", "Paper jam". */
export function frictionDetailWords(kind: FrictionKindName, o: FrictionOccurrence): string {
  return detailWords[kind](o.detail);
}
